import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';
import { ToastController } from 'ionic-angular';

import {
  JackingFormInstanceEditor,
  JackingSectionGroupTemplateEditor,
  JackingEventRowEditor,
  JackingEventRowLegEditor,
  JackingFormLegLayoutTemplateEditor,
} from '../../../../../../models/jacking-v2/jacking.models';
import { PtLuxonDate } from '../../../../../../models/projects/date.models';
import { DateService } from '../../../../../../services/date/date.service';
import {JackingEventsTimeEngine} from '../../../helpers/jacking-events-time-engine.helper';


@Component({
  selector: 'jacking-events-table',
  templateUrl: 'jacking-events-table.component.html'
})
export class JackingEventsTableComponent implements OnInit, OnChanges {
  @Input() editor: JackingFormInstanceEditor;
  @Input() group: JackingSectionGroupTemplateEditor;
  @Input() legsCount: number;
  @Input() isReadonly: boolean = false;
  @Input() dailyLogDate: PtLuxonDate;
  @Input() completedDayByIso: { [isoDate: string]: boolean } = {};
  @Input() activitySpansMultipleDays: boolean = false;
  @Output() viewLegsFreeRequested = new EventEmitter<void>();
  @ViewChild('eventsScroll') eventsScrollRef: ElementRef<HTMLDivElement>;

  private timeEngine: JackingEventsTimeEngine;
  private lastFocusedRowIndex: number | null = null;
  private lastFocusedZone: 'event' | 'time' | 'leg' | 'other' = 'other';
  private tabNavigationPending = false;

  constructor(
    private dateService: DateService,
    private toastCtrl: ToastController
  ) {}

  ngOnInit() {
    this.timeEngine = new JackingEventsTimeEngine(this.dateService, this.toastCtrl, {
      activitySpansMultipleDays: this.activitySpansMultipleDays,
      currentDailyLogDate: this.dailyLogDate,
      onViewLegsFreeRequested: () => this.viewLegsFreeRequested.emit()
    });

    // hydrate all rows once on load
    this.rows.forEach(r => this.timeEngine.hydrateRow(r));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.timeEngine) {
      return;
    }

    if (changes.activitySpansMultipleDays || changes.dailyLogDate) {
      this.timeEngine.updateOptions({
        activitySpansMultipleDays: this.activitySpansMultipleDays,
        currentDailyLogDate: this.dailyLogDate
      });
    }
  }

  get rows(): JackingEventRowEditor[] {
    const rows = this.editor?.jackingEvents?.eventRows || [];
    return rows.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  get orderedLegNumbers(): number[] {
    const layouts: JackingFormLegLayoutTemplateEditor[] = this.editor?.template?.legLayouts || [];
    if (layouts.length) {
      return layouts
        .slice()
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map(l => l.legNumber);
    }

    const count = this.legsCount || 0;
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  // Ensure a row has an entry for each leg number (UI can “fill blanks”)
  getLeg(row: JackingEventRowEditor, legNumber: number): JackingEventRowLegEditor {
    const existing = (row.legs || []).find(l => l.legNumber === legNumber);
    if (existing) {
      return existing;
    }

    const placeholder: JackingEventRowLegEditor = {
      id: 0,
      legNumber,
      legCheckboxValue: false
    };

    row.legs = row.legs || [];
    row.legs.push(placeholder);
    return placeholder;
  }

  showsAnyLegColumns(row: JackingEventRowEditor): boolean {
    return !!(row.hasLegLoad || row.hasLegExtension || row.hasLegPenetration || row.hasRpd);
  }

  rowIsReadonly(row: JackingEventRowEditor): boolean {
    if (this.isReadonly) {
      return true;
    }

    if ((row as any)?._isReadonly !== undefined) {
      return !!(row as any)._isReadonly;
    }

    const currentIsoDate = this.dailyLogDate?.dateTime?.toISODate();
    const rowIsoDate = row?.eventDate?.dateTime?.toISODate();

    return !!rowIsoDate
      && !!currentIsoDate
      && rowIsoDate < currentIsoDate
      && !!this.completedDayByIso?.[rowIsoDate];
  }

  getRowMinDate(row: JackingEventRowEditor): DateTime {
    const currentDay = this.dailyLogDate?.dateTime;
    const formStart = this.editor?.startDate?.dateTime;

    if (!currentDay) {
      return formStart;
    }

    if (!formStart) {
      return currentDay;
    }

    return this.dateService.isFirstDateLargerOffsetsPreApplied(currentDay, formStart)
      ? currentDay
      : formStart;
  }

  // ----- template handlers -----

  async onRowDateChanged(row: JackingEventRowEditor, dt: DateTime) {
    if (this.rowIsReadonly(row)) {
      return;
    }

    await this.timeEngine.onDateChanged(this.editor, this.rows, row, dt);
  }

  async onRowTimeTextChanged(row: JackingEventRowEditor, timeToShow: string) {
    if (this.rowIsReadonly(row)) {
      return;
    }

    await this.timeEngine.onTimeTextChanged(this.editor, this.rows, row, timeToShow);
  }

  async onRowOffsetSelected(row: JackingEventRowEditor, ptLocal: PtLuxonDate) {
    if (this.rowIsReadonly(row)) {
      return;
    }

    await this.timeEngine.onOffsetSelected(this.editor, this.rows, row, ptLocal);
  }

  // In HTML, bind mask per row to avoid the shared-mask bug:
  // [mask]="row._mask"
  getRowMask(row: JackingEventRowEditor): any[] {
    return this.timeEngine.getRowMask(row);
  }

  // ---- title helpers (unchanged) ----

  getTitlePrefix(row: JackingEventRowEditor): string {
    const name = (row?.eventName || '').toString();
    const idx = name.indexOf('...');
    if (idx === -1) {
      return name;
    }
    return name.slice(0, idx).trimEnd();
  }

  getTitleSuffix(row: JackingEventRowEditor): string {
    const name = (row?.eventName || '').toString();
    const idx = name.indexOf('...');
    if (idx === -1) {
      return '';
    }
    return name.slice(idx + 3).trimStart();
  }

  // ---- RPD validation (unchanged) ----

  private parseNullableNumber(v: unknown): number | null {
    if (v === null || v === undefined) {
      return null;
    }

    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) {
        return null;
      }
      const n1 = Number(s);
      return Number.isFinite(n1) ? n1 : null;
    }

    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private isRpdTripletComplete(leg: JackingEventRowLegEditor): boolean {
    const r = this.parseNullableNumber(leg?.r);
    const p = this.parseNullableNumber(leg?.p);
    const d = this.parseNullableNumber(leg?.d);
    return r !== null && p !== null && d !== null;
  }

  public isRpdLegValid(row: JackingEventRowEditor, legNo: number): boolean {
    const leg = this.getLeg(row, legNo);

    // don’t nag until all 3 are entered
    if (!this.isRpdTripletComplete(leg)) {
      return true;
    }

    const r = this.parseNullableNumber(leg?.r);
    const p = this.parseNullableNumber(leg?.p);
    const d = this.parseNullableNumber(leg?.d);

    return r === 0 || p === 0 || d === 0;
  }

  public getInvalidRpdLegNumbers(row: JackingEventRowEditor): number[] {
    if (!row?.hasRpd) {
      return [];
    }

    return (this.orderedLegNumbers || []).filter(legNo => !this.isRpdLegValid(row, legNo));
  }

  public showRpdRuleMessage(row: JackingEventRowEditor): boolean {
    return !!row?.hasRpd && this.getInvalidRpdLegNumbers(row).length > 0;
  }

  public formatLegList(legs: number[]): string {
    return (legs || []).join(', ');
  }

  onGridKeyDown(event: KeyboardEvent): void {
    // Remember whether the next focus change came from keyboard tabbing.
    this.tabNavigationPending = event.key === 'Tab';
  }

  onGridFocusIn(event: FocusEvent): void {
    // Compare the previous and new focus zones so tabbing into the next row can reset horizontal scroll.
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.tabNavigationPending = false;
      return;
    }

    const rowEl = target.closest('.events-row.body') as HTMLElement | null;
    const rowIndexText = rowEl?.getAttribute('data-row-index');
    const rowIndex = rowIndexText !== null ? Number(rowIndexText) : null;
    const zone = this.getFocusZone(target);

    if (
      this.tabNavigationPending &&
      rowIndex !== null &&
      rowIndex !== this.lastFocusedRowIndex &&
      this.lastFocusedZone === 'leg' &&
      (zone === 'event' || zone === 'time')
    ) {
      // When tabbing from right-side leg cells into the next row, reveal the left columns again.
      this.resetHorizontalScroll();
    }

    this.lastFocusedRowIndex = rowIndex;
    this.lastFocusedZone = zone;
    this.tabNavigationPending = false;
  }

  private getFocusZone(target: HTMLElement): 'event' | 'time' | 'leg' | 'other' {
    if (target.closest('.leg-block') || target.closest('.hold-span')) {
      return 'leg';
    }

    if (target.closest('.cell--time')) {
      return 'time';
    }

    if (target.closest('.cell--event')) {
      return 'event';
    }

    return 'other';
  }

  private resetHorizontalScroll(): void {
    const scrollEl = this.eventsScrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollEl.scrollLeft = 0;
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-events/jacking-events-table/jacking-events-table.component.ts